<template>
    <v-container fluid class="pa-4">
        <v-card>
            <v-card-title class="header-card-title">
                <v-btn icon @click="goBack">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <span>Clientes con Factura Electrónica</span>
            </v-card-title>

            <v-card-text class="pa-4">
                <v-row class="mb-4">
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="filters.searchText"
                            :label="searchPlaceholder"
                            prepend-inner-icon="mdi-magnify"
                            clearable
                            dense
                            outlined
                            hide-details
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="filters.type"
                            :items="typeOptions"
                            label="Tipo de persona"
                            clearable
                            dense
                            outlined
                            hide-details
                        />
                    </v-col>
                    <!-- Import Clients From Excel -->
                    <v-col v-if="got_feature('feature_import_clients_from_excel_e_invoice')" cols="12" md="5" class="text-right">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    large
                                    @click="downloadTemplate"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <AppIcon :src="require('@/assets/template_einvoice_customer.svg')" :size="29" :preserveColors="true"/>
                                </v-btn>
                            </template>
                            <span>Descargar Plantilla</span>
                        </v-tooltip>
                        <v-btn color="primary" class="text-none mr-2 import-button" @click="openFileInput">
                            Importar clientes
                        </v-btn>
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".xlsx,.xls"
                            style="display: none"
                            @change="onFileSelected"
                        />
                        <v-progress-circular
                            v-if="jobState === 'running'"
                            indeterminate
                            class="ml-2"
                            size="24"
                            color="primary"
                        ></v-progress-circular>
                        <v-tooltip v-if="are_errors" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    outlined
                                    color="red"
                                    @click="import_errors_dialog = true"
                                    class="black--text error-button text-none"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <AppIcon :src="require('@/assets/view_errors.svg')" :size="24" class="mr-2" style="width: 24px; height: 24px;" :preserveColors="true"/>
                                    Errores
                                </v-btn>
                            </template>
                            <span>Ver errores</span>
                        </v-tooltip>
                    </v-col>
                </v-row>

                <CustomerListTable
                    :items="items"
                    :loading="loading"
                    :total-items="totalItems"
                    :options.sync="options"
                    :filter-type="filters.type"
                    @select-row="selectRow"
                />
            </v-card-text>
        </v-card>

        <div style="display: none;">
            <PartyEditor
                v-if="selectedCustomer"
                ref="partyEditor"
                title="Editar Cliente"
                :party="selectedCustomer"
                :readonly="false"
                :emisor="false"
                :allow_refresh="true"
                :company_id="companydbId"
                @close="closeEditor"
                @saveThird="saveCustomer"
            />
        </div>

        <v-dialog v-model="import_errors_dialog" max-width="500">
            <v-card>
                <v-card-title class="headline title-color">Errores:</v-card-title>
                <v-card-text>
                    <v-row>
                        <template v-for="i in Object.keys(importErrors)">
                            <template v-if="importErrors[i].length">
                                <v-col cols="12" v-for="(error, index) in importErrors[i]" :key="i + '_' + index">
                                    En la línea {{i}}: {{error}}
                                </v-col>
                            </template>
                        </template>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="white" text class="primary-bckground" @click="import_errors_dialog = false">
                        Ok
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">
                    Cerrar
                </v-btn>
            </template>
        </v-snackbar>

    </v-container>
</template>

<script>
import { Party } from '@/modules/facturae/domain/Invoiceable';
import PartyEditor from '@/components/PartyEditor.vue';
import CustomerListTable from '@/components/CustomerListTable.vue';
import {
    SearchCustomersQuery,
    SearchCustomersHandler,
    SaveCustomerCommand,
    SaveCustomerHandler,
    ImportCustomersFromExcelCommand,
    ImportCustomersFromExcelHandler,
    DownloadCustomersTemplateQuery,
    DownloadCustomersTemplateHandler
} from '@/modules/facturae/app/CustomerApplication';
import { ApiCustomerRepository } from '@/modules/facturae/infra/CustomerInfrastructure';
import { Customer } from '@/modules/facturae/domain/CustomerDomain';
import { mapGetters } from 'vuex';

export default {
    name: 'InvoiceCustomersView',
    components: {
        PartyEditor,
        CustomerListTable
    },
    data() {
        return {
            items: [],
            allCustomers: [],
            loading: false,
            totalItems: 0,
            options: {},
            selectedCustomer: null,
            snackbar: false,
            snackbarMessage: '',
            snackbarColor: 'success',
            filters: {
                searchText: '',
                type: null
            },
            typeOptions: [
                { text: 'Persona Física', value: 'fisica' },
                { text: 'Persona Jurídica', value: 'juridica' }
            ],
            searchCustomersHandler: null,
            saveCustomerHandler: null,
            importCustomersHandler: null,
            downloadTemplateHandler: null,
            searchTimeout: null,
            files: null,
            dragging_file: false,
            asyncJobId: null,
            jobState: 'quiet',
            importErrors: {},
            import_errors_dialog: false
        };
    },
    computed: {
        ...mapGetters({
            got_feature: 'got_feature'
        }),
        companydbId() {
            return this.$route.params.companydb_id;
        },
        searchPlaceholder() {
            const placeholders = {
                fisica: 'Buscar por nombre, apellidos o CIF',
                juridica: 'Buscar por razón social, nombre comercial o CIF',
                default: 'Buscar por nombre, apellido, razón social o CIF'
            };
            return placeholders[this.filters.type] || placeholders.default;
        },
        are_errors() {
            let are_errors = false;
            for (let i in this.importErrors) {
                if (this.importErrors[i].length) {
                    are_errors = true;
                }
            }
            return are_errors;
        }
    },
    watch: {
        options: {
            handler() {
                this.loadCustomersPage();
            },
            deep: true
        },
        'filters.searchText': {
            handler() {
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }
                this.searchTimeout = setTimeout(() => {
                    this.triggerSearch();
                }, 300);
            }
        },
        'filters.type': {
            handler() {
                this.triggerSearch();
            }
        }
    },
    created() {
        const repository = new ApiCustomerRepository();
        this.searchCustomersHandler = new SearchCustomersHandler(repository);
        this.saveCustomerHandler = new SaveCustomerHandler(repository);
        this.importCustomersHandler = new ImportCustomersFromExcelHandler(repository);
        this.downloadTemplateHandler = new DownloadCustomersTemplateHandler(repository);
    },
    mounted() {
        this.loadCustomers();
    },
    beforeDestroy() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    },
    methods: {
        async downloadTemplate() {
            try {
                // Create query and execute through handler
                const query = new DownloadCustomersTemplateQuery();
                const url = await this.downloadTemplateHandler.handle(query);

                // Download from URL using blob to control filename
                const res = await fetch(url);
                const blob = await res.blob();

                const objUrl = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');

                link.href = objUrl;
                link.download = 'plantilla_clientes_factura_electronica.xlsx';
                link.click();

                this.snackbarMessage = 'Plantilla descargada correctamente';
                this.snackbarColor = 'success';
                this.snackbar = true;
            } catch (error) {
                console.error('Error descargando plantilla:', error);
                this.snackbarMessage = 'Error al descargar la plantilla';
                this.snackbarColor = 'error';
                this.snackbar = true;
            }
        },
        openFileInput() {
            this.$refs.fileInput.click();
        },
        async onFileSelected(event) {
            const file = event.target.files[0];
            if (!file) return;

            this.files = file;
            await this.importFile(file);

            // Clear the input to allow selecting the same file again
            event.target.value = '';
        },
        async importFile(file) {
            if (!file) return;

            try {
                this.jobState = 'running';

                // Read file as ArrayBuffer
                const fileContent = await this.readFileAsArrayBuffer(file);

                // Create and execute command following DDD pattern
                const command = new ImportCustomersFromExcelCommand(
                    this.companydbId,
                    fileContent,
                    file.name
                );

                const response = await this.importCustomersHandler.handle(command);

                if (response && response.asyncJobId) {
                    this.asyncJobId = response.asyncJobId;
                    this.checkJobStatus();
                } else {
                    this.jobState = 'quiet';
                    this.snackbarMessage = 'Importación completada';
                    this.snackbarColor = 'success';
                    this.snackbar = true;
                    await this.loadCustomers();
                }
            } catch (error) {
                console.error('Error importando archivo:', error);
                this.snackbarMessage = 'Error al importar el archivo';
                this.snackbarColor = 'error';
                this.snackbar = true;
                this.jobState = 'quiet';
            }
        },
        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsArrayBuffer(file);
            });
        },
        async checkJobStatus() {
            if (!this.asyncJobId) return;

            try {
                const { jobs } = await import('@/api/jobs');
                const response = await jobs.getJob({ id: this.asyncJobId });

                const state = response.data.state;

                if (state === 'done') {
                    // Check if there's a nested async_job_id in the output_info
                    const nestedJobId = response.data.output_info && response.data.output_info.async_job_id;

                    if (nestedJobId) {
                        // If there's a nested job, switch to monitoring that one
                        this.asyncJobId = nestedJobId;
                        setTimeout(() => this.checkJobStatus(), 500);
                        return;
                    }

                    this.importErrors = (response.data.output_info && response.data.output_info.errors) || {};

                    const totalLines = Object.keys(this.importErrors).length;
                    const errorLines = Object.keys(this.importErrors).filter(key => this.importErrors[key].length > 0);
                    const successLines = totalLines - errorLines.length;

                    if (this.are_errors) {
                        this.snackbarMessage = `Importación completada: ${successLines} clientes importados, ${errorLines.length} con errores`;
                        this.snackbarColor = 'warning';
                    } else if (totalLines > 0) {
                        this.snackbarMessage = `Importación completada exitosamente: ${successLines} clientes importados`;
                        this.snackbarColor = 'success';
                    } else {
                        this.snackbarMessage = 'Importación completada';
                        this.snackbarColor = 'success';
                    }
                    this.snackbar = true;
                    this.jobState = 'quiet';
                    await this.loadCustomers();
                } else if (state === 'failed' || state === 'error') {
                    this.snackbarMessage = 'Error en la importación';
                    this.snackbarColor = 'error';
                    this.snackbar = true;
                    this.jobState = 'quiet';
                } else if (state === 'running' || state === 'pending') {
                    setTimeout(() => this.checkJobStatus(), 2000);
                } else {
                    this.jobState = 'quiet';
                }
            } catch (error) {
                console.error('Error verificando estado del job:', error);
                this.snackbarMessage = 'Error al verificar el estado de la importación';
                this.snackbarColor = 'error';
                this.snackbar = true;
                this.jobState = 'quiet';
            }
        },
        goBack() {
            this.$router.go(-1);
        },
        triggerSearch() {
            if (this.options.page !== 1) {
                this.options = { ...this.options, page: 1 };
            } else {
                this.loadCustomers();
            }
        },
        selectRow(customer) {

            const partyInstance = new Party({
                id: customer.id,
                company_id: customer.companyId,
                cif: customer.cif || '',
                post_code: customer.postCode || '',
                type: customer.type || 'juridica',
                name: customer.name,
                first_surname: customer.firstSurname,
                second_surname: customer.secondSurname,
                business_name: customer.businessName,
                trade_name: customer.tradeName,
                address: customer.address,
                city: customer.city,
                province: customer.province,
                country: customer.country,
                email: customer.email,
                phone: customer.phone,
                website: customer.website,
                bank_account_number: customer.bankAccountNumber,
                bic: customer.bic,
                DIRe: customer.DIRe,
                accounting_office: customer.accountingOffice,
                management_body: customer.managementBody,
                processing_unit: customer.processingUnit,
                proposing_body: customer.proposingBody,
                mandate_id: customer.mandateId,
                mandate_signature_date: customer.mandateSignatureDate,
                debit_account: customer.debitAccount,
                payment_means: customer.paymentMeans,
                payment_information: customer.paymentInformation,
                pdf_language: customer.pdfLanguage,
                timestamp_modified: customer.timestampModified
            });

            this.$nextTick(() => {
                this.selectedCustomer = partyInstance;

                this.$nextTick(() => {
                    if (this.$refs.partyEditor) {
                        this.$refs.partyEditor.openPartyEditor();
                    }
                });
            });
        },
        closeEditor() {
            this.selectedCustomer = null;
        },
        async saveCustomer(party) {
            try {
                const customer = new Customer({
                    id: party.id,
                    companyId: this.companydbId,
                    cif: party.cif,
                    type: party.type,
                    name: party.name,
                    firstSurname: party.first_surname,
                    secondSurname: party.second_surname,
                    businessName: party.business_name,
                    tradeName: party.trade_name,
                    address: party.address,
                    city: party.city,
                    province: party.province,
                    postCode: party.post_code,
                    email: party.email,
                    phone: party.phone,
                    website: party.website,
                    bankAccountNumber: party.bank_account_number,
                    bic: party.bic,
                    mandateId: party.mandate_id,
                    mandateSignatureDate: party.mandate_signature_date,
                    debitAccount: party.debit_account,
                    paymentMeans: party.payment_means,
                    paymentInformation: party.payment_information,
                    DIRe: party.DIRe,
                    accountingOffice: party.accounting_office,
                    managementBody: party.management_body,
                    processingUnit: party.processing_unit,
                    proposingBody: party.proposing_body,
                    pdfLanguage: party.pdf_language,
                    timestampModified: party.timestamp_modified
                });

                const command = new SaveCustomerCommand(customer);
                await this.saveCustomerHandler.handle(command);

                this.snackbarMessage = 'Cliente guardado correctamente';
                this.snackbarColor = 'success';
                this.snackbar = true;

                await this.loadCustomers();
                this.closeEditor();
                return true;
            } catch (error) {
                console.error('Error guardando cliente:', error);
                this.snackbarMessage = 'Error al guardar el cliente';
                this.snackbarColor = 'error';
                this.snackbar = true;
                return false;
            }
        },
        loadCustomersPage() {
            if (!this.options.page || !this.options.itemsPerPage) return;

            const { page, itemsPerPage, sortBy, sortDesc } = this.options;

            let sortedCustomers = [...this.allCustomers];

            if (sortBy && sortBy.length > 0) {
                const sortField = sortBy[0];
                const isDescending = sortDesc && sortDesc[0];

                sortedCustomers.sort((a, b) => {
                    let valueA = this.getSortValue(a, sortField);
                    let valueB = this.getSortValue(b, sortField);

                    if (valueA === null || valueA === undefined) valueA = '';
                    if (valueB === null || valueB === undefined) valueB = '';

                    let comparison = 0;
                    if (typeof valueA === 'string' && typeof valueB === 'string') {
                        comparison = valueA.localeCompare(valueB, 'es', { sensitivity: 'base' });
                    } else {
                        comparison = valueA < valueB ? -1 : (valueA > valueB ? 1 : 0);
                    }

                    return isDescending ? -comparison : comparison;
                });
            }

            const start = (page - 1) * itemsPerPage;
            const end = start + itemsPerPage;

            this.items = sortedCustomers.slice(start, end);
        },
        getSortValue(customer, field) {
            if (field === 'fullName') {
                return customer.getFullName ? customer.getFullName() : '';
            }
            if (field === 'businessName') {
                return customer.getBusinessName ? customer.getBusinessName() : customer.businessName || '';
            }
            return customer[field] || '';
        },
        async loadCustomers() {
            this.loading = true;
            try {
                const query = new SearchCustomersQuery(
                    this.companydbId,
                    this.filters.searchText || undefined,
                    this.filters.type || undefined
                );
                const customers = await this.searchCustomersHandler.handle(query);

                this.allCustomers = customers;
                this.totalItems = customers.length;
                this.loadCustomersPage();

            } catch (error) {
                console.error('Error cargando clientes:', error);
                this.snackbarMessage = 'Error al cargar el listado de clientes';
                this.snackbarColor = 'error';
                this.snackbar = true;
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.header-card-title {
    width: fit-content;
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;

    span {
        font-weight: bold;
        color: var(--v-primary-base);
    }
}

.dragging_file {
    background-color: rgba(102, 53, 155, 0.1);
    border: 2px dashed var(--v-primary-base);
    border-radius: 8px;
    transition: all 0.3s ease;
}

.file-input--outlined {
    ::v-deep .v-input__control {
        border-radius: 8px;
    }
}

.title-color {
    color: var(--v-primary-base);
}

.primary-bckground {
    background-color: var(--v-primary-base) !important;
}

.import-button {
    border-radius: 10px !important;
}

.error-button {
    border-radius: 10px !important;
    border: 1.111px solid #DC0B0B !important;
    background: #FFF1F1 !important;
}
</style>